import { useMemo } from "react";
import type { MarkdownStyle } from "@-/com.npmjs.react-native-enriched-markdown";
import { HANIVERSE_COMPOSITE_FONT_FAMILY } from "@-/dev.improve.haniverse.assets.graphemes.fonts";
import { useCSSVariable } from "@-/com.npmjs.uniwind";

/**
 * Every text block names the app's own family explicitly. `EnrichedMarkdownText` is a NATIVE text
 * view: it lays out its own attributed string, so neither uniwind's `font-sans` default nor
 * `expo-font-fallback`'s cascade (which only wraps React Native's text layout) reaches it. Without
 * this it rendered in the system font, and every glyph only Han Composite carries — the private-use
 * stroke components a mnemonic mentions inline, rare extension-block characters — showed as a
 * LastResort box inside the description while the same character drew fine one line above.
 * The Regular face carries every glyph the app ships, so naming the family is the whole fix.
 */
const textFont = { fontFamily: HANIVERSE_COMPOSITE_FONT_FAMILY } as const;

const WHITESPACE_REGEX = /\s+/;

function hslFromChannels(channels: string | number | undefined, fallback: string): string {
  if (channels === undefined || channels === "") {
    return fallback;
  }
  const value = String(channels);
  if (value.startsWith("hsl") || value.startsWith("#") || value.startsWith("rgb")) {
    return value;
  }
  const [hue = "0", saturation = "0%", lightness = "0%"] = value.trim().split(WHITESPACE_REGEX);
  return `hsl(${hue}, ${saturation}, ${lightness})`;
}

/** `tone: "muted"` draws body paragraphs in the muted foreground, for secondary prose. */
export function useThemedMarkdownStyle(
  size: "base" | "sm" | "mnemonic" = "base",
  { tone = "foreground" }: { tone?: "foreground" | "muted" } = {},
): MarkdownStyle {
  const foreground = useCSSVariable("--color-foreground");
  const mutedForeground = useCSSVariable("--color-muted-foreground");
  const border = useCSSVariable("--color-border");
  const muted = useCSSVariable("--color-muted");

  return useMemo(() => {
    const fg = hslFromChannels(foreground, "#1F2937");
    const mutedFg = hslFromChannels(mutedForeground, "#4B5563");
    const borderColor = hslFromChannels(border, "#D1D5DB");
    const mutedBg = hslFromChannels(muted, "#F9FAFB");

    // Native markdown lays out attributed paragraphs, not the outer React Native view.
    // Keep the mnemonic's glyph badges inside its line box on every platform.
    const bodySize =
      size === "base" ? {} : { fontSize: 14, lineHeight: size === "mnemonic" ? 22 : 21 };

    return {
      ...(size === "mnemonic"
        ? { code: { ...textFont, backgroundColor: mutedBg, borderColor, color: fg, fontSize: 14 } }
        : {}),
      blockquote: {
        ...textFont,
        ...bodySize,
        backgroundColor: mutedBg,
        borderColor,
        color: mutedFg,
        padding: 12,
      },
      h1: { ...textFont, color: fg },
      h2: { ...textFont, color: fg },
      h3: { ...textFont, color: fg },
      h4: { ...textFont, color: fg },
      h5: { ...textFont, color: mutedFg },
      h6: { ...textFont, color: mutedFg },
      inlineMath: { color: fg },
      link: { ...textFont, color: fg, underline: true },
      list: { ...textFont, ...bodySize, bulletColor: mutedFg, color: fg, markerColor: mutedFg },
      math: { color: fg },
      paragraph: { ...textFont, ...bodySize, color: tone === "muted" ? mutedFg : fg },
      table: { borderColor, color: fg, headerTextColor: fg },
      underline: { color: fg },
    } satisfies MarkdownStyle;
  }, [foreground, mutedForeground, border, muted, size, tone]);
}
